import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Directive, ElementRef, OnDestroy, PLATFORM_ID, Renderer2, effect, inject, input } from '@angular/core';

/** Overlap between panel and anchor, matching the `bottom-2` offset the panels used before. */
const ANCHOR_OVERLAP_PX = 8;
/** Gap kept between the panel and the bottom edge of the viewport. */
const VIEWPORT_MARGIN_PX = 8;
/** Upper bound for the panel height, matching the `max-h-64` the panels used before. */
const MAX_PANEL_HEIGHT_PX = 256;

/**
 * Pins a dropdown panel below its trigger, positioned against the viewport.
 *
 * An absolutely positioned panel belongs to the scrollable overflow of every ancestor
 * scroll container: inside a modal body it grows that body's scroll height and is clipped
 * by its `overflow`, so opening a select makes the whole modal scroll. A `fixed` panel is
 * positioned against the viewport instead and does neither — the modal keeps its capped
 * height and its own scrollbar.
 *
 * The host element must carry `fixed`; this directive only writes `top`, `left`, `width`
 * and `max-height`. No ancestor may establish a containing block for fixed elements
 * (`transform`, `filter`, `perspective`, `contain`), or the panel would be clipped again.
 *
 * @example
 * ```html
 * <button #trigger (click)="toggleSelect()">…</button>
 * <div class="fixed" [gsAnchoredPanel]="trigger" [gsAnchoredPanelOpen]="opened()">…</div>
 * ```
 */
@Directive({
  selector: '[gsAnchoredPanel]',
  standalone: true
})
export class GsAnchoredPanelDirective implements OnDestroy {
  // Services
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly document = inject(DOCUMENT);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  // Inputs & Models
  /** Trigger element the panel is pinned below. */
  anchor = input.required<HTMLElement>({ alias: 'gsAnchoredPanel' });
  /** Whether the panel is open. Opening measures the anchor and starts following it. */
  open = input(false, { alias: 'gsAnchoredPanelOpen' });

  // Properties & Signals
  /** Detaches the scroll/resize listeners, or `undefined` while the panel is closed. */
  private stopTracking?: () => void;

  constructor() {
    // Follow the anchor while the panel is open, drop the listeners when it closes
    effect(() => (this.open() ? this.track() : this.release()));
  }

  /** Lifecycle: drops the scroll/resize listeners when the panel is destroyed. */
  ngOnDestroy(): void {
    this.release();
  }

  // Methods
  /** Positions the panel and keeps it on the anchor while the page or a container scrolls. */
  private track(): void {
    if (!this.isBrowser || this.stopTracking) return;

    this.reposition();
    const onViewportChange = (): void => this.reposition();
    // Capture phase: `scroll` does not bubble, so inner scroll containers are only reachable here
    const scrollOptions: AddEventListenerOptions = { capture: true, passive: true };
    this.document.addEventListener('scroll', onViewportChange, scrollOptions);
    this.document.defaultView?.addEventListener('resize', onViewportChange, { passive: true });
    this.stopTracking = (): void => {
      this.document.removeEventListener('scroll', onViewportChange, scrollOptions);
      this.document.defaultView?.removeEventListener('resize', onViewportChange);
    };
  }

  /** Stops following the anchor. */
  private release(): void {
    this.stopTracking?.();
    this.stopTracking = undefined;
  }

  /** Writes the anchor's current viewport coordinates onto the panel. */
  private reposition(): void {
    const view = this.document.defaultView;
    if (!view) return;

    const rect = this.anchor().getBoundingClientRect();
    const top = rect.bottom - ANCHOR_OVERLAP_PX;
    const available = view.innerHeight - top - VIEWPORT_MARGIN_PX;
    const element = this.host.nativeElement;

    this.renderer.setStyle(element, 'top', `${top}px`);
    this.renderer.setStyle(element, 'left', `${rect.left}px`);
    this.renderer.setStyle(element, 'width', `${rect.width}px`);
    this.renderer.setStyle(element, 'max-height', `${Math.max(0, Math.min(MAX_PANEL_HEIGHT_PX, available))}px`);
  }
}
